<template>
    <div class="horarios-acesso-editor">
        <div style="color: #909399; font-size: 12px; margin-bottom: 12px;">
            Defina, por perfil e por dia da semana, a janela de horário em que o login é permitido. Fora da janela o acesso é bloqueado.
            <b>Acesso total</b> = 24h liberado; <b>Bloqueado</b> = dia inteiro sem acesso. A hora final é exclusiva (ex.: 08h–18h libera até 17:59).
            <br>NTI-PMVC (admin) nunca é bloqueado por horário.
        </div>

        <div v-for="perfil in perfis" :key="perfil.key" class="perfil-bloco">
            <div class="perfil-cabecalho">
                <span class="perfil-titulo">{{ perfil.label }}</span>
                <el-button size="mini" plain @click="replicarPrimeiro(perfil.key)">
                    <i class="el-icon-copy-document"></i> Replicar segunda-feira para todos os dias
                </el-button>
            </div>

            <table class="tabela-horarios">
                    <thead>
                        <tr>
                            <th style="width: 150px;">Dia</th>
                            <th style="width: 190px;">Acesso</th>
                            <th>Início</th>
                            <th>Fim</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="dia in dias" :key="dia.key">
                            <td class="dia">{{ dia.label }}</td>
                            <td>
                                <el-select v-model="estado[perfil.key][dia.key].situacao" size="small" style="width: 175px;">
                                    <el-option value="total" label="Acesso total (24h)"></el-option>
                                    <el-option value="faixa" label="Faixa de horário"></el-option>
                                    <el-option value="bloqueado" label="Bloqueado"></el-option>
                                </el-select>
                            </td>
                            <td>
                                <el-select v-if="estado[perfil.key][dia.key].situacao === 'faixa'" v-model="estado[perfil.key][dia.key].inicio" size="small" style="width: 90px;">
                                    <el-option v-for="h in horasInicio" :key="h" :value="h" :label="rotuloHora(h)"></el-option>
                                </el-select>
                                <span v-else class="vazio">—</span>
                            </td>
                            <td>
                                <el-select v-if="estado[perfil.key][dia.key].situacao === 'faixa'" v-model="estado[perfil.key][dia.key].fim" size="small" style="width: 90px;">
                                    <el-option v-for="h in horasFim" :key="h" :value="h" :label="rotuloHora(h)"></el-option>
                                </el-select>
                                <span v-else class="vazio">—</span>
                            </td>
                        </tr>
                </tbody>
            </table>
        </div>

        <div style="margin-top: 14px;">
            <el-button type="primary" size="small" @click="salvar"><i class="el-icon-check"></i> Salvar horários de acesso</el-button>
            <span v-if="erro" style="color: #f56c6c; font-size: 12px; margin-left: 10px;">{{ erro }}</span>
        </div>
    </div>
</template>

<script>
// Perfis (batem com o enforcement do backend) e dias (0=Domingo ... 6=Sábado, como o dayOfWeek).
const PERFIS = [
    { key: 'secretaria', label: 'Secretaria — painel administrativo (Secretário, Diretor, Coordenador, Vice-Diretor)' },
    { key: 'professor',  label: 'Professor — diário eletrônico' },
    { key: 'aluno',      label: 'Aluno — portal do aluno' }
]
// Chave = 'd' + dayOfWeek (0=Domingo ... 6=Sábado). O prefixo 'd' evita que o PHP
// serialize o objeto por dia como lista (chaves inteiras 0..6 sequenciais viram array).
const DIAS = [
    { key: 'd1', label: 'Segunda-feira' },
    { key: 'd2', label: 'Terça-feira' },
    { key: 'd3', label: 'Quarta-feira' },
    { key: 'd4', label: 'Quinta-feira' },
    { key: 'd5', label: 'Sexta-feira' },
    { key: 'd6', label: 'Sábado' },
    { key: 'd0', label: 'Domingo' }
]

export default {
    name: 'HorariosAcessoEditor',
    props: {
        p: { type: Object, required: true },
        valor: { default: null }
    },
    data () {
        return {
            perfis: PERFIS,
            dias: DIAS,
            horasInicio: Array.from({ length: 24 }, (v, i) => i),     // 0..23
            horasFim: Array.from({ length: 24 }, (v, i) => i + 1),    // 1..24
            estado: {},
            erro: ''
        }
    },
    watch: {
        valor: { immediate: true, handler (v) { this.estado = this.parse(v) } }
    },
    methods: {
        rotuloHora (h) { return String(h).padStart(2, '0') + 'h' },
        diaPadrao () { return { situacao: 'total', inicio: 8, fim: 18 } },
        num (v, padrao) { const n = Number(v); return isNaN(n) ? padrao : n },
        parse (v) {
            let obj = {}
            if (v) {
                try { obj = typeof v === 'string' ? JSON.parse(v) : v } catch (e) { obj = {} }
            }
            if (typeof obj !== 'object' || obj === null || Array.isArray(obj)) obj = {}

            const estado = {}
            PERFIS.forEach(perfil => {
                estado[perfil.key] = {}
                const regra = obj[perfil.key]

                // Distingue: faixa legada [inicio,fim]; lista indexada por dia (dado antigo
                // serializado como array); ou objeto por dia ({ d0: {...}, ... }).
                let faixaLegada = null
                let porIndice = null
                if (Array.isArray(regra)) {
                    if (regra.length && typeof regra[0] === 'object' && regra[0] !== null) {
                        porIndice = regra   // [ {dia0}, {dia1}, ... ] com índice = dayOfWeek
                    } else {
                        faixaLegada = regra // [inicio, fim]
                    }
                }

                DIAS.forEach(dia => {
                    let cfg = this.diaPadrao()
                    let d = null
                    if (faixaLegada) {
                        cfg = { situacao: 'faixa', inicio: this.num(faixaLegada[0], 8), fim: this.num(faixaLegada[1], 18) }
                    } else {
                        if (porIndice) {
                            d = porIndice[Number(dia.key.replace('d', ''))]
                        } else if (regra && typeof regra === 'object') {
                            d = regra[dia.key]
                        }
                        if (d && typeof d === 'object') {
                            if (d.liberado === false) {
                                cfg = { situacao: 'bloqueado', inicio: 8, fim: 18 }
                            } else if (this.num(d.inicio, 0) === 0 && this.num(d.fim, 24) === 24) {
                                cfg = { situacao: 'total', inicio: 8, fim: 18 }
                            } else {
                                cfg = { situacao: 'faixa', inicio: this.num(d.inicio, 8), fim: this.num(d.fim, 18) }
                            }
                        }
                    }
                    estado[perfil.key][dia.key] = cfg
                })
            })
            return estado
        },
        replicarPrimeiro (perfilKey) {
            const base = this.estado[perfilKey][DIAS[0].key]
            DIAS.forEach(dia => {
                this.$set(this.estado[perfilKey], dia.key, { situacao: base.situacao, inicio: base.inicio, fim: base.fim })
            })
        },
        serializar () {
            const out = {}
            PERFIS.forEach(perfil => {
                out[perfil.key] = {}
                DIAS.forEach(dia => {
                    const c = this.estado[perfil.key][dia.key]
                    if (c.situacao === 'bloqueado') {
                        out[perfil.key][dia.key] = { liberado: false }
                    } else if (c.situacao === 'total') {
                        out[perfil.key][dia.key] = { liberado: true, inicio: 0, fim: 24 }
                    } else {
                        out[perfil.key][dia.key] = { liberado: true, inicio: Number(c.inicio), fim: Number(c.fim) }
                    }
                })
            })
            return out
        },
        validar () {
            for (let i = 0; i < PERFIS.length; i++) {
                for (let j = 0; j < DIAS.length; j++) {
                    const c = this.estado[PERFIS[i].key][DIAS[j].key]
                    if (c.situacao === 'faixa' && Number(c.fim) <= Number(c.inicio)) {
                        this.erro = `${PERFIS[i].label.split(' ')[0]} / ${DIAS[j].label}: a hora final deve ser maior que a inicial.`
                        return false
                    }
                }
            }
            this.erro = ''
            return true
        },
        salvar () {
            if (!this.validar()) return
            this.$emit('save', this.serializar())
        }
    }
}
</script>

<style scoped>
.perfil-bloco {
    margin-bottom: 22px;
}
.perfil-cabecalho {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    padding: 6px 0;
    margin-bottom: 6px;
    border-bottom: 2px solid #ebeef5;
}
.perfil-titulo {
    font-size: 14px;
    font-weight: 600;
    color: #303133;
}
.tabela-horarios {
    width: 100%;
    border-collapse: collapse;
}
.tabela-horarios th, .tabela-horarios td {
    border-bottom: 1px solid #f0f0f0;
    padding: 6px 8px;
    text-align: left;
    font-size: 13px;
}
.tabela-horarios th {
    color: #909399;
    font-weight: 600;
}
.tabela-horarios td.dia {
    color: #303133;
    font-weight: 600;
}
.tabela-horarios .vazio {
    color: #c0c4cc;
}
</style>
